import { Box, Flex, Text, VStack, useBreakpointValue } from '@chakra-ui/react';
import { addMonths, format } from 'date-fns';
import { ReactNode, useState } from 'react';

import { useOnceWhen } from '../../../../../../hooks';
import {
  trackGateDecisionClicked,
  trackGateEditDetailsOpened,
  trackGateViewed,
} from '../../../../../../services/analytics/marketplaceEvents';
import { formatMoney } from '../../../../../../utils/helpers';
import { PayoffTerm } from '../../PayoffTooltipCopy';
import {
  GateDecision,
  GateVariant,
  LeadGuidance,
  getBuyoutTaxLine,
  getGateVariant,
  toEstimatedMoney,
} from '../../guidance';
import { payoffNoun } from '../../hooks/usePayoffDisplay';
import CostCallout from '../CostCallout';
import ConfirmBuyoutModal from './ConfirmBuyoutModal';
import EditLeaseDetailsModal from './EditLeaseDetailsModal';
import GateChoiceCard from './GateChoiceCard';
import useGateDecision from './useGateDecision';

type GateScreenProps = {
  guidance: LeadGuidance;
  externalId: string;
  dealId: string;
  leadId: string;
  model?: string | null;
};

type BreakpointCopy = { base: string; md: string };

type GateCopy = { heading: BreakpointCopy; subheading: string; footnote: BreakpointCopy };

const getGateCopy = (variant: GateVariant, payoffVerified: boolean): GateCopy => {
  const copyByVariant: Record<GateVariant, GateCopy> = {
    two_way: {
      heading: {
        base: 'Two good ways out. Pick yours.',
        md: 'Two good ways out of this lease. Pick yours.',
      },
      subheading:
        "Your listing isn't live yet. Your payoff is higher than what cars like yours are selling for right now, so pick your path before buyers see it.",
      footnote: {
        base: 'You can change your mind before you accept an offer or start the buyout.',
        md: `Both numbers use your ${payoffNoun(
          payoffVerified,
        )}. You can change your mind before you accept an offer or start the buyout.`,
      },
    },
    three_way: {
      heading: {
        base: 'Three ways out. Pick yours.',
        md: 'Three ways out of this lease. Pick yours.',
      },
      subheading:
        "Your listing isn't live yet. Your payoff is well above what cars like yours are selling for right now, so pick your path before buyers see it.",
      footnote: {
        base: 'You can change your mind before anything is final.',
        md: 'You can change your mind before anything is final.',
      },
    },
  };
  return copyByVariant[variant];
};

const PAYOFF_TERM = 'payoff';

const withPayoffTooltip = (sentence: string) => {
  const termStart = sentence.indexOf(PAYOFF_TERM);
  if (termStart < 0) return sentence;
  return (
    <>
      {sentence.slice(0, termStart)}
      <PayoffTerm />
      {sentence.slice(termStart + PAYOFF_TERM.length)}
    </>
  );
};

const EditDetailsLink = ({
  onClick,
  fontWeight = '600',
  children,
}: {
  onClick: () => void;
  fontWeight?: string;
  children: ReactNode;
}) => (
  <Text
    as="button"
    type="button"
    fontWeight={fontWeight}
    color="leaseEndBlue"
    textDecoration="underline"
    textUnderlineOffset="3px"
    onClick={onClick}
  >
    {children}
  </Text>
);

const GateScreen = ({ guidance, externalId, dealId, leadId, model }: GateScreenProps) => {
  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false;
  const { record, pendingDecision, errorMessage } = useGateDecision(externalId);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isConfirmBuyoutOpen, setIsConfirmBuyoutOpen] = useState(false);

  const variant = getGateVariant(guidance);
  const copy = getGateCopy(variant, guidance.payoffVerified);
  const text = (entry: BreakpointCopy) => (isMobile ? entry.base : entry.md);

  useOnceWhen(leadId !== '', () => trackGateViewed(dealId, leadId, variant));

  const handleDecision = async (decision: GateDecision) => {
    trackGateDecisionClicked(dealId, leadId, variant, decision);
    return record(decision);
  };

  // Buyout puts the listing on hold, so it confirms first; the parked screen carries the call.
  const handleConfirmBuyout = async () => {
    const succeeded = await handleDecision('buyout');
    if (succeeded) setIsConfirmBuyoutOpen(false);
  };

  const handleOpenEdit = () => {
    trackGateEditDetailsOpened(dealId, leadId);
    setIsEditOpen(true);
  };

  const cardProps = (decision: GateDecision) => ({
    onSelect: () => handleDecision(decision),
    isPending: pendingDecision === decision,
    isDisabled: pendingDecision != null,
  });

  const buyoutCardProps = {
    onSelect: () => setIsConfirmBuyoutOpen(true),
    isPending: false,
    isDisabled: pendingDecision != null,
  };

  const payoffAmount =
    guidance.buyout != null ? formatMoney(guidance.buyout.payoff, { noCents: true }) : null;
  const taxLine =
    guidance.buyout != null ? getBuyoutTaxLine(guidance.buyout.taxAndFees) : undefined;
  const premiumAboveValue =
    guidance.buyout != null ? toEstimatedMoney(guidance.buyout.premium) : null;
  const sellOutOfPocket =
    guidance.sellNowNet != null ? toEstimatedMoney(guidance.sellNowNet) : null;

  const paymentsKnown = guidance.monthlyPayment != null && guidance.remainingMonths != null;
  const leaseEndLabel =
    guidance.remainingMonths != null
      ? format(addMonths(new Date(), guidance.remainingMonths), 'MMM yyyy')
      : null;

  const sellCard = (
    <GateChoiceCard
      label="Sell it anyway"
      headline="List it, and a buyer takes the car."
      callout={
        sellOutOfPocket != null && (
          <CostCallout
            prefix="about"
            amount={sellOutOfPocket}
            footer="out of your own pocket at signing"
            mt="12px"
          />
        )
      }
      body={
        variant === 'three_way'
          ? 'Very few cars this far under sell for enough to close the gap. If you need out now and can write the check, listing is still here for you.'
          : isMobile
          ? `Offers rarely cover a gap this size, so plan on a check around ${sellOutOfPocket} at signing. Still the fastest way out of the car.`
          : `Offers for cars this far under their payoff usually don't cover the gap, so most sellers here write a check around ${sellOutOfPocket} at signing. If you want out of the car, this is still the fastest way.`
      }
      buttonLabel="List my car"
      {...cardProps('list_anyway')}
    />
  );

  const finishDetail = paymentsKnown ? (
    <Text fontSize="11.5px" color="grayLight" lineHeight="1.5" mt="12px">
      Based on your{' '}
      <Text as="b" color="leaseEndBlue">
        {formatMoney(guidance.monthlyPayment, { noCents: true })}
      </Text>
      /mo payment, lease ending {leaseEndLabel}.{' '}
      <EditDetailsLink onClick={handleOpenEdit}>Not right? Edit</EditDetailsLink>
    </Text>
  ) : (
    <Box
      mt="12px"
      bg="yellowGlow"
      borderLeftWidth="3px"
      borderLeftColor="leaseEndYellow"
      borderRightRadius="10px"
      px="12px"
      py="10px"
      fontSize="12.5px"
      color="charcoal"
      lineHeight="1.5"
    >
      Have your monthly payment and lease-end date handy?{' '}
      <EditDetailsLink onClick={handleOpenEdit} fontWeight="700">
        Add them
      </EditDetailsLink>{' '}
      and we'll show you this path in real dollars.
    </Box>
  );

  return (
    <VStack
      spacing={0}
      px={{ base: '18px', md: '40px' }}
      pt={{ base: '34px', md: '54px' }}
      pb={{ base: '40px', md: '60px' }}
    >
      <Text
        fontSize={{ base: '23px', md: '30px' }}
        fontWeight="700"
        color="leaseEndBlue"
        letterSpacing="-0.015em"
        lineHeight="1.15"
        textAlign="center"
      >
        {text(copy.heading)}
      </Text>
      <Text
        fontSize="14.5px"
        color="charcoal"
        lineHeight="1.55"
        textAlign="center"
        maxW="56ch"
        mt="10px"
      >
        {withPayoffTooltip(copy.subheading)}
      </Text>

      <Flex
        direction={{ base: 'column', md: 'row' }}
        gap={{ base: '14px', md: '18px' }}
        w="100%"
        maxW="880px"
        mt={{ base: '24px', md: '32px' }}
        align="stretch"
      >
        {variant === 'three_way' ? (
          <>
            <GateChoiceCard
              label="Ride it out"
              recommended
              headline={
                guidance.finishLeasePaymentsLeft != null ? (
                  <>
                    Finish your lease: about {toEstimatedMoney(guidance.finishLeasePaymentsLeft)} in
                    payments left
                  </>
                ) : (
                  'Finish your lease'
                )
              }
              body={
                isMobile
                  ? 'Keep driving, make your remaining payments, and hand the keys back at the end like normal.'
                  : 'Keep driving, make your remaining payments, and hand the keys back at the end like normal. When a buyout costs this much more than the car is worth, finishing is usually the cheapest way through.'
              }
              detail={finishDetail}
              buttonLabel="Finish my lease"
              {...cardProps('finish_lease')}
            />
            <GateChoiceCard
              label="Love the car? Keep it"
              headline={<>Own it for about {payoffAmount}</>}
              taxLine={taxLine}
              body={
                <>
                  That's about {premiumAboveValue} more than the car's current value, so it only
                  makes sense if you plan to drive it for years to come. Our team walks you through
                  your exact total.
                </>
              }
              buttonLabel="Explore the buyout"
              {...buyoutCardProps}
            />
            {sellCard}
          </>
        ) : (
          <>
            <GateChoiceCard
              label="Keep the car"
              recommended
              headline={
                isMobile ? (
                  <>Own it for about {payoffAmount}</>
                ) : (
                  <>
                    Own your {model || 'car'} for about {payoffAmount}
                  </>
                )
              }
              taxLine={taxLine}
              body={
                isMobile
                  ? 'The buyout pays off the lease and puts the title in your name. No selling, and you keep the car you know. Our team walks you through your exact total.'
                  : 'A lease buyout pays off the lease and puts the title in your name. No selling, no check to write today, and you keep the car you know. Our team walks you through your exact total.'
              }
              buttonLabel="Start my buyout"
              {...buyoutCardProps}
            />
            {sellCard}
          </>
        )}
      </Flex>

      {errorMessage != null && (
        <Text fontSize="12.5px" color="leaseEndRed" mt="14px" textAlign="center">
          {errorMessage}
        </Text>
      )}

      <Text
        fontSize="12px"
        color="grayLight"
        textAlign="center"
        maxW="60ch"
        lineHeight="1.5"
        mt="22px"
      >
        {text(copy.footnote)}
      </Text>

      {isConfirmBuyoutOpen && (
        <ConfirmBuyoutModal
          isOpen
          onClose={() => setIsConfirmBuyoutOpen(false)}
          onConfirm={handleConfirmBuyout}
          variant={variant}
          isPending={pendingDecision === 'buyout'}
          errorMessage={errorMessage}
          dealId={dealId}
          leadId={leadId}
          model={model}
          payoffAmount={payoffAmount}
          taxLine={taxLine}
          premiumAboveValue={premiumAboveValue}
        />
      )}

      {isEditOpen && (
        <EditLeaseDetailsModal
          isOpen
          onClose={() => setIsEditOpen(false)}
          externalId={externalId}
          monthlyPayment={guidance.monthlyPayment}
          remainingMonths={guidance.remainingMonths}
        />
      )}
    </VStack>
  );
};

export default GateScreen;
